import { formatTime } from "@xola/ui-kit";
import { ColDef, ColumnState } from "ag-grid-community";
import { DateFormat, formatDate } from "common";
import { EquipmentResource, Event, Experience, Guide } from "types";
import create from "zustand";
import { GroupedData } from "./useDashboardTableData";
import { queryClient } from "@/api/queryClient";
import { xolaUri } from "@/api/xolaUri";
import { getStart } from "@/models/Event";
import { getDefaultPhotoUrl } from "@/models/Product";
import { DashboardGroupBy } from "@/types/Dashboard";

type GroupHeaderData = {
    name: string;
    imageData?: string;
    sortKey: string;
};

export interface DashboardTableGroupState {
    isLoading: boolean;
    groupHeaderData: Record<string, GroupHeaderData>;
    isColumnDefsCustomized: boolean;
    rowToRefresh?: ID;
    printData: {
        groupedData: GroupedData[];
        columnDefs: ColDef[];
    } | null;
    columnState?: ColumnState[];
    pinnedColumns?: ColumnState[];
    selectedTableView: number | string | null;
    actions: {
        loadGroupHeader: (groupBy: DashboardGroupBy, groupLabel: string) => void;
        resetGroupHeaderData: () => void;
        setPrintData: (data: { groupedData: GroupedData[]; columnDefs: ColDef[] }) => void;
        resetPrintData: () => void;
        setColumnDefs: (columnDefs: ColDef[]) => void;
        setIsColumnDefsCustomized: (isCustomized: boolean) => void;
        setColumnState: (state: ColumnState[]) => void;
        resetColumnState: () => void;
        setTableView: (view: number | string | null) => void;
    };
}

export const useDashboardTableStore = create<DashboardTableGroupState>((set, get) => ({
    isLoading: false,
    groupHeaderData: {},
    printData: null,
    rowToRefresh: undefined,
    isColumnDefsCustomized: false,
    columnState: undefined,
    pinnedColumns: undefined,
    selectedTableView: null,
    actions: {
        loadGroupHeader(groupBy, groupLabel) {
            const key = groupLabel;
            if (get().groupHeaderData[key]) return;

            let meta: Partial<GroupHeaderData> = {};

            switch (groupBy) {
                case DashboardGroupBy.PRODUCT: {
                    const product = queryClient.getQueryData<Experience>(["experiences", groupLabel]);
                    meta = {
                        ...meta,
                        name: product?.name ?? groupLabel,
                        imageData: product ? getDefaultPhotoUrl(product) : "",
                        sortKey: product?.name,
                    };
                    break;
                }
                case DashboardGroupBy.GUIDE: {
                    const guide = queryClient.getQueryData<Guide>(["guides", groupLabel]);
                    meta = {
                        ...meta,
                        name: guide?.name ?? groupLabel,
                        imageData: guide ? getGuidePhotoUrl(guide) : "",
                        sortKey: guide?.name,
                    };
                    break;
                }
                case DashboardGroupBy.EQUIPMENT: {
                    const equipment = queryClient.getQueryData<EquipmentResource>(["resources", groupLabel]);
                    meta = {
                        ...meta,
                        name: equipment?.name ?? groupLabel,
                        sortKey: equipment?.name,
                    };
                    break;
                }
                case DashboardGroupBy.EVENT: {
                    const event = queryClient.getQueryData<Event>(["events", groupLabel]);
                    const experience = queryClient.getQueryData<Experience>(["experiences", event?.experience.id]);
                    const isStartTime = event && getStart(event) && experience?.scheduleType !== "day";
                    const timeTitle =
                        (isStartTime ? formatDate(getStart(event), DateFormat.TIME, false) : "No Fixed Times") +
                        " - " +
                        event?.title;
                    const timeSortKey =
                        (isStartTime ? formatDate(getStart(event), DateFormat.TIME_SHORT).padStart(4, "0") : "0") +
                        " - " +
                        event?.title;
                    meta = {
                        ...meta,
                        name: timeTitle,
                        imageData: experience ? getDefaultPhotoUrl(experience, "small") : "",
                        sortKey: timeSortKey,
                    };
                    break;
                }

                case DashboardGroupBy.ARRIVAL_TIME: {
                    const isAllDay = groupLabel === "No Fixed Times";
                    const paddedTime = isAllDay ? "0" : groupLabel.padStart(4, "0");
                    meta = {
                        ...meta,
                        name: isAllDay ? groupLabel : formatTime(groupLabel, DateFormat.TIME),
                        sortKey: paddedTime,
                    };
                    break;
                }
                case DashboardGroupBy.PICKUP: {
                    meta = {
                        ...meta,
                        name: groupLabel,
                        sortKey: groupLabel,
                    };
                }
            }

            set((state) => ({
                groupHeaderData: { ...state.groupHeaderData, [key]: meta as GroupHeaderData },
            }));
        },
        resetGroupHeaderData() {
            set({ groupHeaderData: {} });
        },
        setPrintData: (data) =>
            set((state) => ({
                printData: {
                    groupedData: data.groupedData,
                    columnDefs:
                        data.columnDefs && data.columnDefs.length > 0
                            ? data.columnDefs
                            : state.printData?.columnDefs ?? [],
                },
            })),
        setColumnDefs: (columnDefs) =>
            set((state) => ({
                printData: {
                    groupedData: state.printData?.groupedData ?? [],
                    columnDefs,
                },
            })),

        setIsColumnDefsCustomized: (isCustomized) => set({ isColumnDefsCustomized: isCustomized }),
        setColumnState: (state: ColumnState[]) => set({ columnState: state }),
        resetColumnState: () => set({ columnState: undefined }),
        resetPrintData: () => set({ printData: null }),
        setTableView: (view) => set({ selectedTableView: view }),
    },
}));

const getGuidePhotoUrl = (guide: Guide) => {
    return guide?.picture?.id
        ? `${xolaUri}/api/sellers/${guide.seller?.id}/guides/${guide.id}/picture?size=medium`
        : undefined;
};
